import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import auth from "./auth";
import app from "../firebaseConfig";
import { Chart } from "react-google-charts";
import InvestmentModal from "./InvestmentModal";

import Slider from "react-slick";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

// Icons
import { Heart, Share } from "react-feather";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";

// Local fallback background
import back2 from "./back2.png";

const tagColors = {
  "One of One": "#00c9ff",
  Limited: "orange",
  Rare: "#B400FF",
  "Cash Flow": "#42D010",
};

const db = getFirestore(app);

/**
 * Generate an array of "YYYY-MM" strings from 2 years ago up to the current month.
 */
const generatePlaceholderData = () => {
  const today = new Date();
  const twoYearsAgo = new Date(today.getFullYear() - 2, today.getMonth(), 1);
  const placeholderMonths = [];
  let current = new Date(twoYearsAgo);

  while (current <= today) {
    const yyyy = current.getFullYear();
    const mm = String(current.getMonth() + 1).padStart(2, "0");
    placeholderMonths.push(`${yyyy}-${mm}`);
    current.setMonth(current.getMonth() + 1);
  }
  return placeholderMonths;
};

const CarDetailsPage = () => {
  const { carID } = useParams();
  const navigate = useNavigate();

  // Background image loaded from Firestore (fallback is back2)
  const [bgURL, setBgURL] = useState(back2);

  const [carDetails, setCarDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("6");
  const [investors, setInvestors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [amountOwned, setAmountOwned] = useState(0);
  const [allCarIDs, setAllCarIDs] = useState([]);
  const [investmentAmount, setInvestmentAmount] = useState(null);

  const placeholderMonths = generatePlaceholderData();

  // For the slideshow
  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    centerMode: true,
    centerPadding: "180px",
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchBackground = async () => {
      try {
        const docRef = doc(db, "background", "1");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.picture) {
            setBgURL(data.picture);
          }
        }
      } catch (err) {
        console.error("Error fetching background:", err);
      }
    };
    fetchBackground();
  }, []);

  useEffect(() => {
    const fetchAmountOwned = async () => {
      if (!user || !carID) return;
      try {
        const investQ = query(
          collection(db, "investments"),
          where("userID", "==", user.uid),
          where("carID", "==", carID)
        );
        const snap = await getDocs(investQ);
        let sum = 0;
        snap.forEach((d) => {
          sum += d.data().amount || 0;
        });
        setAmountOwned(sum);
      } catch (error) {
        console.error("Error fetching amount owned:", error);
      }
    };
    fetchAmountOwned();
  }, [user, carID]);

  useEffect(() => {
    const fetchAllCarIDs = async () => {
      try {
        const cSnap = await getDocs(collection(db, "cars"));
        const ids = cSnap.docs.map((docu) => docu.id);
        setAllCarIDs(ids);
      } catch (err) {
        console.error(err);
      }
    };
    fetchAllCarIDs();
  }, []);

  useEffect(() => {
    const fetchCarDetails = async () => {
      try {
        const carRef = doc(db, "cars", carID);
        const carDoc = await getDoc(carRef);

        if (!carDoc.exists()) {
          console.error("No such car found!");
          setCarDetails(null);
          setLoading(false);
          return;
        }

        const carData = carDoc.data();

        const payoutQ = query(
          collection(db, "payouts"),
          where("carID", "==", carID)
        );
        const payoutSnap = await getDocs(payoutQ);
        const monthlyMap = {};

        payoutSnap.forEach((pDoc) => {
          const pData = pDoc.data();
          if (!pData.date || !pData.amount) return;
          const d = pData.date.toDate();
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const ym = `${y}-${m}`;

          if (!monthlyMap[ym]) monthlyMap[ym] = 0;
          monthlyMap[ym] += pData.amount;
        });

        const revenueHistory = placeholderMonths.map((ym) => ({
          month: ym,
          revenue: monthlyMap[ym] || 0,
        }));

        const investorsQ = query(
          collection(db, "users"),
          where("investedCars", "array-contains", carID)
        );
        const investorsSnap = await getDocs(investorsQ);
        const investorData = investorsSnap.docs.map((docu) => {
          const dData = docu.data();
          return {
            id: docu.id,
            name: dData.private ? "Anonymous" : dData.name,
            photoURL: dData.private
              ? "/path/to/anonymous-placeholder.png"
              : dData.photoURL,
          };
        });

        setCarDetails({
          ...carData,
          revenueHistory,
        });
        setInvestors(investorData);
      } catch (error) {
        console.error("Error fetching car details:", error);
        setCarDetails(null);
      } finally {
        setLoading(false);
      }
    };

    fetchCarDetails();
  }, [carID]);

  useEffect(() => {
    const fetchInvestment = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser || !carID) return;

      const investmentsRef = collection(db, "investments");
      const q = query(
        investmentsRef,
        where("carID", "==", carID),
        where("userID", "==", currentUser.uid)
      );

      const querySnapshot = await getDocs(q);
      let totalAmount = 0;
      querySnapshot.forEach((doc) => {
        // Assuming each document has an "amount" field representing the investment amount
        totalAmount += doc.data().amount;
      });

      setInvestmentAmount(totalAmount);
    };

    fetchInvestment();
  }, [auth.currentUser, carID]);

  const handleInvestClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  if (loading) {
    return <p>Loading car details...</p>;
  }
  if (!carDetails) {
    return <p>No car found for car</p>;
  }

  const {
    carName = "Car Name",
    tags = [],
    pictures = [],
    totalValue = 0,
    moneyRaised = 0,
    averageROI = 0,
    revenueHistory = [],
    location = "Unknown",
    details = "No additional details available.",
    amountReceivedThisMonth = 0,
    lifetimeReturns = 0,
    maintenanceCost = 200,
    insurance = 100,
    rent,        
    risk,        
    "12month": projectedROI12Months = 0,  
    "24month": projectedROI24Months = 0,  
    vin,
    maintenanceHistory,
    mileage,
    additionalDetails = "",
  } = carDetails;

  const progress = Math.min((moneyRaised / totalValue) * 100, 100);

  const filterRevenueData = () => {
    const cutoffDate = new Date();
    const monthsBack = timeRange === "all" ? 1200 : parseInt(timeRange, 10);
    cutoffDate.setMonth(cutoffDate.getMonth() - monthsBack);

    return revenueHistory.filter(({ month }) => {
      const [y, m] = month.split("-");
      const monthDate = new Date(parseInt(y), parseInt(m) - 1, 1);
      return monthDate >= cutoffDate;
    });
  };

  const graphData = [
    ["Month", "Payouts"],
    ...filterRevenueData().map(({ month, revenue }) => {
      const [y, m] = month.split("-");
      return [new Date(+y, +m - 1, 1), revenue];
    }),
  ];

  const currentIndex = allCarIDs.indexOf(carID);
  const prevCarID = currentIndex > 0 ? allCarIDs[currentIndex - 1] : null;
  const nextCarID =
    currentIndex < allCarIDs.length - 1 ? allCarIDs[currentIndex + 1] : null;

  const mainTagForGlow = tags && tags.length > 0 ? tags[0] : null;
  const glowColor = mainTagForGlow && tagColors[mainTagForGlow] ? tagColors[mainTagForGlow] : "#ffffff";

  return (
    <div
      style={{
        backgroundImage: `linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0) 0%,
          rgba(255, 255, 255, 1) 85%
        ), url(${bgURL})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top",
        backgroundSize: "cover",
        minHeight: "100vh",
      }}
    >
      {prevCarID && (
        <button
          onClick={() => navigate(`/cars/${prevCarID}`)}
          style={{
            position: "fixed",
            top: "50%",
            left: "200px",
            transform: "translateY(-50%)",
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            zIndex: 9999,
          }}
        >
          <FaArrowLeft size={40} color="#007bff" />
        </button>
      )}
      {nextCarID && (
        <button
          onClick={() => navigate(`/cars/${nextCarID}`)}
          style={{
            position: "fixed",
            top: "50%",
            right: "200px",
            transform: "translateY(-50%)",
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            zIndex: 9999,
          }}
        >
          <FaArrowRight size={40} color="#007bff" />
        </button>
      )}

<div
  style={{
    padding: "20px",
    maxWidth: "1000px",
    margin: "auto",
    background: "linear-gradient(111deg,rgb(10, 10, 10),rgb(51, 51, 51),rgb(17, 17, 19))", // Linear gradient with three colors
    boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
    fontFamily: "'Roboto', sans-serif",
    color: "white",
  }}
>
  <div style={{ position: "relative", marginBottom: "20px" }}>
          <button
            onClick={() => navigate("/marketplace")}
            style={{
              position: "absolute",
              left: 0,
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "#007bff",
              color: "#fff",
              padding: "10px 20px",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              boxShadow: "0 4px 8px rgba(0,0,0,0.6)",  // Added shadow
            }}
          >
            Back
          </button>
          <h1 style={{ textAlign: "center", margin: 0, fontSize: "2.5em", fontWeight: "bold" }}>
            {carName}
            {tags && tags.length > 0 && (
              <span style={{ marginLeft: "10px" }}>
                {tags.map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      backgroundColor: tagColors[tag] || "#666",
                      color: "white",
                      borderRadius: "4px",
                      padding: "2px 6px",
                      marginLeft: "5px",
                      fontSize: "0.6em",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </span>
            )}
          </h1>
        </div>

        <hr style={{ margin: "20px 0", border: "none", borderBottom: "0px solid #ddd" }} />

        <div style={{ position: "relative", textAlign: "center", marginBottom: "20px" }}>
          <style>{`
            @keyframes glowAnimation {
              0%, 90%, 100% { opacity: 0; }
              95% { opacity: 1; }
            }
            .carousel .control-arrow.control-prev { left: -50px; }
            .carousel .control-arrow.control-next { right: -50px; }
          `}</style>
          {/* Glow Effect */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "90%",
              height: "calc(100% + 20px)",
              backgroundColor: glowColor,
              filter: "blur(20px)",
              opacity: 0,
              animation: "glowAnimation 10s infinite",
              zIndex: -1,
            }}
          ></div>
          <Carousel
            showThumbs={false}
            infiniteLoop
            autoPlay
            interval={5000}
            stopOnHover
            showArrows
            showStatus={false}
            swipeable
            emulateTouch
            dynamicHeight={false}
          >
            {pictures && pictures.length > 0 ? (
              pictures.map((picture, index) => (
                <div key={index}>
                  <img
                    src={picture || "https://via.placeholder.com/800"}
                    alt={`${carName} - Slide ${index + 1}`}
                    style={{
                      maxWidth: "120%",
                      borderRadius: "10px",
                      objectFit: "cover",
                      maxHeight: "600px",
                      margin: "auto",
                    }}
                  />
                </div>
              ))
            ) : (
              <div>
                <img
                  src="https://via.placeholder.com/800"
                  alt="Placeholder"
                  style={{
                    maxWidth: "120%",
                    borderRadius: "10px",
                    objectFit: "cover",
                    maxHeight: "600px",
                    margin: "auto",
                  }}
                />
                <p>No images available</p>
              </div>
            )}
          </Carousel>
          <div style={{ position: "absolute", top: "10px", right: "10px", display: "flex", gap: "10px" }}>
            <Heart
              color="white"
              size={32}
              style={{ cursor: "pointer" }}
              onClick={() => { console.log("Heart clicked!"); }}
            />
            <Share
              color="white"
              size={32}
              style={{ cursor: "pointer" }}
              onClick={() => { console.log("Share clicked!"); }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            flexWrap: "wrap",
            marginBottom: "30px",
          }}
        >
          <div style={{ flex: "1", display: "flex", flexDirection: "column", maxWidth: "120px", marginRight: "50px" }}>
            <p style={{ fontWeight: "bold", fontSize: "1.1em", margin: 0 }}>
              Location: {location}
            </p>
            <button
              style={{
                backgroundColor: "#007bff",
                color: "#fff",
                padding: "8px 8px",
                fontSize: ".8em",
                borderRadius: "5px",
                border: "none",
                cursor: "pointer",
                marginTop: "10px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.6)",
              }}
              onClick={() => navigate("/hub/dallas")}
            >
              More Details
            </button>
            <button
              style={{
                backgroundColor: "#007bff",
                color: "#fff",
                padding: "8px 8px",
                fontSize: ".8em",
                borderRadius: "5px",
                border: "none",
                cursor: "pointer",
                marginTop: "10px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.6)",
              }}
              onClick={() => navigate(`/rent/${carID}`)}
            >
              Rent
            </button>
          </div>

          <div style={{ flex: "1", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div
              style={{
                backgroundColor: "#333",
                borderRadius: "10px",
                overflow: "hidden",
                height: "30px",
                width: "650px",
                position: "relative",
                marginBottom: "10px",
                marginLeft: "auto",
                marginRight: "auto",
                paddingLeft: "20px",
                marginTop: "10px",
                boxShadow: "inset 0 2px 5px rgba(0,0,0,0.6)",
              }}
            >
              <div
                style={{
                  backgroundColor: progress >= 100 ? "#007bff" : "#32B720",
                  height: "100%",
                  width: `${progress}%`,
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />
              <span
                style={{
                  position: "absolute",
                  width: "100%",
                  left: 0,
                  textAlign: "center",
                  fontWeight: "bold",
                  lineHeight: "30px",
                  fontSize: "18px",
                  color: "white",
                }}
              >
                {`${Math.round(progress)}%`}
              </span>
            </div>

            <p
              style={{
                fontSize: "1em",
                fontWeight: "bold",
                marginTop: "20px",
                marginBottom: "10px",
                textAlign: "center",
                boxShadow: "0px 0px 0px rgba(0,0,0,0.5)",
                padding: "10px",
                borderRadius: "5px",
              }}
            >
              <span style={{ fontSize: "2em" }}>
                ${moneyRaised.toLocaleString()}
              </span>{" "}
              <span style={{ fontSize: "1.4em", margin: "0 5px" }}>
                out of
              </span>{" "}
              <span style={{ fontSize: "2em" }}>
                ${totalValue.toLocaleString()}
              </span>
              <br />
              <span style={{ display: "block", fontSize: "1.2em", marginTop: "8px" }}>
                Allocated
              </span>
            </p>

            {progress < 100 ? (
           <button
           style={{
             background: "linear-gradient(85deg,rgb(57, 172, 44),rgb(10, 226, 10))", // Gradient from green to dark green
             color: "#fff",
             border: "none",
             borderRadius: "60px",
             padding: "28px 50px",
             fontSize: "2em",
             fontWeight: "bold",
             cursor: "pointer",
             marginTop: "20px",
             boxShadow: "inset 0 2px 5px rgba(0,0,0,0.5), 0px 4px 15px rgba(0,0,0,0.6)",
           }}
           onClick={handleInvestClick}
         >
           Invest
         </button>
         
            ) : (
              <button
  style={{
    background: "linear-gradient(135deg, #007bff, #00c6ff)", // Linear gradient from blue to light blue
    color: "#fff",
    border: "none",
    borderRadius: "60px",
    padding: "28px 50px",
    fontSize: "2em",
    fontWeight: "bold",
    cursor: "pointer",
    marginTop: "20px",
  }}
  onClick={() => navigate(`/rent/${carID}`)}
>
  Rent
</button>

            )}
          </div>

          <div style={{ flex: "1", textAlign: "right" }}>
            <div
              style={{
                backgroundColor: "#007bff",
                color: "white",
                padding: "10px",
                borderRadius: "10px",
                fontSize: "2.5em",
                fontWeight: "bold",
                width: "80px",
                height: "80px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 8px rgba(0,0,0,0.6)",
              }}
            >
              {averageROI}%
            </div>
            <p style={{ fontSize: "1em", marginTop: "10px" }}>Average ROI</p>
          </div>
        </div>

        <div
          style={{
            margin: "20px 0",
            width: "98%",
            textAlign: "Left",
            backgroundColor: "#1e1e1e",
            padding: "15px",
            borderRadius: "10px",
            color: "white",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}
        >
          <h3 style={{ marginBottom: "10px" }}>Monthly Payout & Revenue</h3>
          <p style={{ fontSize: "1.2em", margin: "8px 0" }}>
            Amount Received This Month:{" "}
            <span style={{ fontSize: "1.4em", fontWeight: "bold", color: "#4caf50" }}>
              ${amountReceivedThisMonth.toLocaleString()}
            </span>
          </p>
          <p style={{ fontSize: "1.2em", margin: "8px 0" }}>
            ROI This Month:{" "}
            <span style={{ fontSize: "1.4em", fontWeight: "bold", color: "#4caf50" }}>
              {totalValue > 0 ? ((amountReceivedThisMonth / totalValue) * 100).toFixed(2) : "N/A"}%
            </span>
          </p>
          <p style={{ fontSize: "1.2em", margin: "8px 0" }}>
            Amount Owned:{" "}
            <span style={{ fontSize: "1.4em", fontWeight: "bold", color: "#4caf50" }}>
              ${amountOwned.toLocaleString()}
            </span>
          </p>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "20px",
            marginBottom: "40px",
          }}
        >
          {/* Key Investment Stats */}
          <div
            style={{
              flex: "1 1 300px",
              border: "1px solid #1e1e1e",
              borderRadius: "10px",
              padding: "20px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Key Investment Stats</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee" }}>
                    Rent or Flip
                  </th>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                    {rent || "Rent"}
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee" }}>
                    Risk Level
                  </th>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                    {risk || "Moderate"}
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee" }}>
                    12-Mo Projected ROI
                  </th>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                    {projectedROI12Months || 0}%
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px" }}>
                    24-Mo Projected ROI
                  </th>
                  <td style={{ padding: "8px" }}>
                    {projectedROI24Months || 0}%
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Financial Details */}
          <div
            style={{
              flex: "1 1 300px",
              border: "1px solid #ddd",
              borderRadius: "10px",
              padding: "20px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Financial Details</h3>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee" }}>
                    Total Received
                  </th>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                    ${lifetimeReturns || 0}
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee" }}>
                    Received This Month
                  </th>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                    ${amountReceivedThisMonth || 0}
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee" }}>
                    Monthly Maintenance Cost
                  </th>
                  <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                    ${maintenanceCost || 0}
                  </td>
                </tr>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px" }}>
                    Monthly Insurance Cost
                  </th>
                  <td style={{ padding: "8px" }}>
                    ${insurance || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Additional Car Information */}
        <div
          style={{
            margin: "20px 0",
            padding: "20px",
            backgroundColor: "#1e1e1e",
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}
        >
          <h2 style={{ marginBottom: "10px", color: "#007bff" }}>
            Additional Car Information
          </h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee" }}>
                  VIN
                </th>
                <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                  {vin || "N/A"}
                </td>
              </tr>
              <tr>
                <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid #eee" }}>
                  Mileage
                </th>
                <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                  {mileage || "N/A"} miles
                </td>
              </tr>
              <tr>
                <th style={{ textAlign: "left", padding: "8px" }}>
                  Maintenance History
                </th>
                <td style={{ padding: "8px" }}>
                  {maintenanceHistory || "No maintenance history available."}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Investment Summary */}
        <div
          style={{
            margin: "20px 0",
            padding: "20px",
            backgroundColor: "#1e1e1e",
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}
        >
          <h2 style={{ marginBottom: "10px", color: "#007bff" }}>
            Investment Summary
          </h2>
          {additionalDetails ? (
            <p
              style={{
                marginBottom: "10px",
                lineHeight: "1.6",
                color: "white",
                whiteSpace: "pre-line",
              }}
            >
              {additionalDetails}
            </p>
          ) : (
            <p style={{ color: "white" }}>
              No additional details available for this car.
            </p>
          )}
        </div>

        {/* Investors at the bottom */}
        <div
          style={{
            margin: "20px 0",
            padding: "20px",
            backgroundColor: "#1e1e1e",
            borderRadius: "10px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
          }}
        >
          <h3 style={{ color: "#007bff" }}>Investors ({investors.length})</h3>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "10px",
              flexWrap: "wrap",
            }}
          >
            {investors.slice(0, 5).map((inv, index) => (
              <div key={index} style={{ textAlign: "center" }}>
                <img
                  src={inv.photoURL}
                  alt={inv.name}
                  style={{
                    width: "50px",
                    height: "50px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    marginBottom: "5px",
                  }}
                />
                <p style={{ fontSize: "0.9em", color: "white" }}>{inv.name}</p>
              </div>
            ))}
            {investors.length > 5 && (
              <p style={{ fontSize: "1.2em", fontWeight: "bold", color: "white" }}>
                +{investors.length - 5}
              </p>
            )}
          </div>
        </div>

        {isModalOpen && (
          <InvestmentModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            carName={carDetails.carName}
            carID={carID}
            moneyRaised={carDetails.moneyRaised}
            totalValue={carDetails.totalValue}
          />
        )}

        {investmentAmount !== null && (
          <div className="investment-info">
            <p>
              You have invested: <strong>${investmentAmount}</strong> in this car.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CarDetailsPage;
